import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "./api";

const TOKEN_KEY = "music-space-session-token";

function readStoredToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function storeToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private browsing or restricted storage should not block auth state.
  }
}

export function useAuth() {
  const [token, setToken] = useState(readStoredToken);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(readStoredToken()));
  const [error, setError] = useState("");

  const applySession = useCallback((session) => {
    storeToken(session.token);
    setToken(session.token);
    setUser(session.user);
    setError("");
    return session;
  }, []);

  const clearSession = useCallback(() => {
    storeToken("");
    setToken("");
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    apiRequest("/api/auth/me", { token })
      .then((payload) => {
        if (!cancelled) {
          setUser(payload.user);
          setError("");
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          clearSession();
          setError(requestError.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clearSession, token]);

  const login = useCallback(
    async ({ email, password, captchaToken }) => {
      const session = await apiRequest("/api/auth/login", {
        method: "POST",
        body: { email, password, captchaToken },
      });
      return applySession(session);
    },
    [applySession]
  );

  const register = useCallback(
    async ({ name, email, password, role, instrument, instruments, captchaToken }) => {
      const session = await apiRequest("/api/auth/register", {
        method: "POST",
        body: { name, email, password, role, instrument, instruments, captchaToken },
      });
      return applySession(session);
    },
    [applySession]
  );

  const logout = useCallback(async () => {
    const currentToken = token;
    clearSession();
    if (currentToken) {
      try {
        await apiRequest("/api/auth/logout", { method: "POST", token: currentToken });
      } catch {
        // The local session is already cleared; a stale server token can expire naturally.
      }
    }
  }, [clearSession, token]);

  return { user, token, loading, error, login, register, logout };
}
